import type { FeatureCollection } from "geojson";
import { Geo } from "@rogoag/geo";
import type { geojson, Mission, MissionTypeEnum } from "@rogoag/mission";
import type { IAirtableAttachment } from "@domain/CRM/Attachment";
import type { Job } from "@domain/CRM/job";
import type { IAttachmentDownloader } from "@ports/browser/IAttachmentDownloader";
import type { IMissionLoader } from "@ports/missions/IMissionLoader";

export interface MissionsLoaderDeps {
	downloader: IAttachmentDownloader;
}

// The mission package carries a ~2 MB WASM core. Loading it on first use keeps it off the app's
// critical path — only the job panel and the ops maps ever parse a mission.
const missionPackage = () => import("@rogoag/mission");

const MISSION_TYPES: Record<string, MissionTypeEnum> = { grid: "Grid", modgrid: "ModGrid", zone: "Zone" };

/** A deal's `sitesType` as the mission model's type; undefined (let the model infer) when unrecognised. */
function missionTypeOf(sitesType: string | undefined): MissionTypeEnum | undefined {
	return sitesType ? MISSION_TYPES[sitesType.trim().toLowerCase()] : undefined;
}

const EMPTY_FC: FeatureCollection = { type: "FeatureCollection", features: [] };

/** The package's GeoJSON aliases have no GeometryCollection member; the shapes are otherwise the
 *  RFC 7946 ones `@types/geojson` describes, and the model rejects what it can't use. */
const asMissionFc = (fc: FeatureCollection) => fc as unknown as geojson.FeatureCollection;

/**
 * Stateful adapter that implements `IMissionLoader` by downloading attachment
 * bytes through the injected `IAttachmentDownloader` and parsing them with
 * `@rogoag/mission`. Per-instance `WeakMap` caches mean repeated calls for the
 * same `Job` reference within a session skip the network.
 *
 * Lives in 4-Infrastructure because the cache is real instance state and the
 * adapter is the bridge between an IO port (downloader) and the mission
 * parser. The Application layer talks to the `IMissionLoader` port, not this
 * class.
 */
export class MissionsLoader implements IMissionLoader {
	private readonly exeCache = new WeakMap<Job, Mission>();
	private readonly intCache = new WeakMap<Job, Mission>();
	private readonly ptsShpCache = new WeakMap<Job, Mission>();

	constructor(private readonly deps: MissionsLoaderDeps) {}

	async parse(bytes: Uint8Array): Promise<Mission> {
		const { Mission } = await missionPackage();
		return new Mission(bytes);
	}

	async loadExe(job: Job): Promise<Mission | undefined> {
		const cached = this.exeCache.get(job);
		if (cached) return cached;
		const mission = await this.loadKmlMission(job, "exeMission");
		if (mission) this.exeCache.set(job, mission);
		return mission;
	}

	async loadInt(job: Job): Promise<Mission | undefined> {
		const cached = this.intCache.get(job);
		if (cached) return cached;
		const mission = await this.loadKmlMission(job, "intMission");
		if (mission) this.intCache.set(job, mission);
		return mission;
	}

	async loadPtsShp(
		job: Job,
		expectedType?: string,
	): Promise<Mission | "no-attachment" | "download-failed" | "parse-failed"> {
		const cached = this.ptsShpCache.get(job);
		if (cached) return cached;

		const ptsAttachments = job.attachments?.pointsSHP ?? [];
		const zonesAttachments = job.attachments?.zonesSHP ?? [];
		if (ptsAttachments.length + zonesAttachments.length === 0) return "no-attachment";

		const [points, zones] = await Promise.all([
			this.downloadFiles(job, ptsAttachments),
			this.downloadFiles(job, zonesAttachments),
		]);
		if (points.length + zones.length === 0) {
			console.error(
				`Pts SHP load: every attachment download returned empty for job ${job.id}`,
				[...ptsAttachments, ...zonesAttachments].map((a) => a.filename),
			);
			return "download-failed";
		}

		try {
			const { Mission } = await missionPackage();
			// `idProperty` mirrors exit-interview-lambda's `selectedIdColumn: this.job.selectedSampleColumn`.
			// Without it the model auto-detects the id column from a fixed list of names, so SHPs that key
			// on e.g. "sp_number" would be ambiguous or unmatched.
			const pointsFc = points.length > 0 ? await Geo.toGeoJSON(points) : EMPTY_FC;
			const zonesFc = zones.length > 0 ? await Geo.toGeoJSON(zones) : undefined;
			const mission = Mission.fromPoints(asMissionFc(pointsFc), {
				idProperty: job.selectedSampleColumn || undefined,
				zonesFc: zonesFc && asMissionFc(zonesFc),
				missionType: missionTypeOf(expectedType),
				name: job.fieldName,
			});
			this.ptsShpCache.set(job, mission);
			return mission;
		} catch (err) {
			console.error(
				`Pts SHP parse failed for job ${job.id}`,
				{ filenames: [...points, ...zones].map((f) => f.name), expectedType },
				err,
			);
			return "parse-failed";
		}
	}

	/** Download each attachment as a named File (the SHP converter pairs sidecars by name). */
	private async downloadFiles(job: Job, attachments: readonly IAirtableAttachment[]): Promise<File[]> {
		const files = await Promise.all(
			attachments.map(async (att) => {
				if (!att.url) return null;
				try {
					const data = await this.deps.downloader.load<ArrayBuffer>(att, job.id, "arraybuffer");
					return data ? new File([data], att.filename) : null;
				} catch (err) {
					console.error(`Pts SHP attachment download failed (${att.filename}):`, err);
					return null;
				}
			}),
		);
		return files.filter((f) => f !== null);
	}

	private async loadKmlMission(job: Job, attachmentKey: "exeMission" | "intMission"): Promise<Mission | undefined> {
		const attachment = job.attachments?.[attachmentKey]?.[0];
		if (!attachment?.url) return undefined;

		// KML or KMZ alike: the model sniffs a KMZ archive by its magic bytes.
		const data = await this.deps.downloader.load<ArrayBuffer>(attachment, job.id, "arraybuffer");
		if (!data) return undefined;

		try {
			const { Mission } = await missionPackage();
			return new Mission(new Uint8Array(data));
		} catch (err) {
			console.warn(`Failed to parse ${attachmentKey} ${attachment.filename}:`, err);
			return undefined;
		}
	}
}
